import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { StateContext } from "./state";

type TripType = "oneWay" | "return" | "both";
type DestinationType = "hotel" | "address";
type TransportType = "private" | "shared" | null;
type LuggageOption = "yes" | "no";

interface CouponValidation {
  valid: boolean;
  coupon: { code: string; type: string; value: number };
  originalAmount: number;
  discountAmount: number;
  finalAmount: number;
}

interface TransferContextValue {
  tripType: TripType;
  setTripType: React.Dispatch<React.SetStateAction<TripType>>;
  arrivalDate: string | null;
  setArrivalDate: React.Dispatch<React.SetStateAction<string | null>>;
  arrivalTime: string;
  setArrivalTime: React.Dispatch<React.SetStateAction<string>>;
  returnDate: string | null;
  setReturnDate: React.Dispatch<React.SetStateAction<string | null>>;
  returnTime: string;
  setReturnTime: React.Dispatch<React.SetStateAction<string>>;
  destinationType: DestinationType;
  setDestinationType: React.Dispatch<React.SetStateAction<DestinationType>>;
  address: string;
  setAddress: React.Dispatch<React.SetStateAction<string>>;
  selectedHotelId: string;
  setSelectedHotelId: React.Dispatch<React.SetStateAction<string>>;
  zone: any | null;
  setZone: React.Dispatch<React.SetStateAction<any | null>>;
  passengers: number;
  setPassengers: React.Dispatch<React.SetStateAction<number>>;
  adults: number;
  setAdults: React.Dispatch<React.SetStateAction<number>>;
  childrenUnder4: number;
  setChildrenUnder4: React.Dispatch<React.SetStateAction<number>>;
  childrenOver4: number;
  setChildrenOver4: React.Dispatch<React.SetStateAction<number>>;
  transportType: TransportType;
  setTransportType: React.Dispatch<React.SetStateAction<TransportType>>;
  luggage: number;
  setLuggage: React.Dispatch<React.SetStateAction<number>>;
  arrivalSchedule: string;
  setArrivalSchedule: React.Dispatch<React.SetStateAction<string>>;
  returnSchedule: string;
  setReturnSchedule: React.Dispatch<React.SetStateAction<string>>;
  firstName: string;
  setFirstName: React.Dispatch<React.SetStateAction<string>>;
  lastName: string;
  setLastName: React.Dispatch<React.SetStateAction<string>>;
  contactEmail: string;
  setContactEmail: React.Dispatch<React.SetStateAction<string>>;
  phone: string;
  setPhone: React.Dispatch<React.SetStateAction<string>>;
  originCity: string;
  setOriginCity: React.Dispatch<React.SetStateAction<string>>;
  flightNumber: string;
  setFlightNumber: React.Dispatch<React.SetStateAction<string>>;
  returnFlightNumber: string;
  setReturnFlightNumber: React.Dispatch<React.SetStateAction<string>>;
  hasAdditionalLuggage: LuggageOption;
  setHasAdditionalLuggage: React.Dispatch<React.SetStateAction<LuggageOption>>;
  additionalComments: string;
  setAdditionalComments: React.Dispatch<React.SetStateAction<string>>;
  couponCode: string;
  setCouponCode: React.Dispatch<React.SetStateAction<string>>;
  couponValidation: CouponValidation | null;
  setCouponValidation: React.Dispatch<
    React.SetStateAction<CouponValidation | null>
  >;
  resetTransfer: () => void;
}

interface TransferProviderProps {
  children: React.ReactNode;
}

const TransferContext = createContext<TransferContextValue>(
  {} as TransferContextValue,
);

export const useTransferProvider = () => useContext(TransferContext);

function getDefaultContactInfo(user: any) {
  return {
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    contactEmail: user?.email || "",
    phone: user?.phone || "",
  };
}

export function TransferProvider({ children }: TransferProviderProps) {
  const globalState = useContext(StateContext);
  const defaultContact = useMemo(
    () => getDefaultContactInfo(globalState.user),
    [globalState.user],
  );

  const [tripType, setTripType] = useState<TripType>("oneWay");
  const [arrivalDate, setArrivalDate] = useState<string | null>(null);
  const [arrivalTime, setArrivalTime] = useState<string>("");
  const [returnDate, setReturnDate] = useState<string | null>(null);
  const [returnTime, setReturnTime] = useState<string>("");
  const [destinationType, setDestinationType] =
    useState<DestinationType>("hotel");
  const [address, setAddress] = useState<string>("");
  const [selectedHotelId, setSelectedHotelId] = useState<string>("");
  const [zone, setZone] = useState<any | null>(null);
  const [passengers, setPassengers] = useState<number>(1);
  const [adults, setAdults] = useState<number>(1);
  const [childrenUnder4, setChildrenUnder4] = useState<number>(0);
  const [childrenOver4, setChildrenOver4] = useState<number>(0);
  const [transportType, setTransportType] = useState<TransportType>(null);
  const [luggage, setLuggage] = useState<number>(1);
  const [arrivalSchedule, setArrivalSchedule] = useState<string>("");
  const [returnSchedule, setReturnSchedule] = useState<string>("");

  const [firstName, setFirstName] = useState<string>(defaultContact.firstName);
  const [lastName, setLastName] = useState<string>(defaultContact.lastName);
  const [contactEmail, setContactEmail] = useState<string>(
    defaultContact.contactEmail,
  );
  const [phone, setPhone] = useState<string>(defaultContact.phone || "");
  const [originCity, setOriginCity] = useState<string>("");
  const [flightNumber, setFlightNumber] = useState<string>("");
  const [returnFlightNumber, setReturnFlightNumber] = useState<string>("");
  const [hasAdditionalLuggage, setHasAdditionalLuggage] =
    useState<LuggageOption>("no");
  const [additionalComments, setAdditionalComments] = useState<string>("");
  const [couponCode, setCouponCode] = useState<string>("");
  const [couponValidation, setCouponValidation] =
    useState<CouponValidation | null>(null);

  const resetTransfer = useCallback(() => {
    const freshContact = getDefaultContactInfo(globalState.user);
    setTripType("oneWay");
    setArrivalDate(null);
    setArrivalTime("");
    setReturnDate(null);
    setReturnTime("");
    setDestinationType("hotel");
    setAddress("");
    setSelectedHotelId("");
    setZone(null);
    setPassengers(1);
    setAdults(1);
    setChildrenUnder4(0);
    setChildrenOver4(0);
    setTransportType(null);
    setLuggage(1);
    setArrivalSchedule("");
    setReturnSchedule("");
    setFirstName(freshContact.firstName);
    setLastName(freshContact.lastName);
    setContactEmail(freshContact.contactEmail);
    setPhone(freshContact.phone || "");
    setOriginCity("");
    setFlightNumber("");
    setReturnFlightNumber("");
    setHasAdditionalLuggage("no");
    setAdditionalComments("");
    setCouponCode("");
    setCouponValidation(null);
  }, [globalState.user]);

  const value = useMemo<TransferContextValue>(
    () => ({
      tripType,
      setTripType,
      arrivalDate,
      setArrivalDate,
      arrivalTime,
      setArrivalTime,
      returnDate,
      setReturnDate,
      returnTime,
      setReturnTime,
      destinationType,
      setDestinationType,
      address,
      setAddress,
      selectedHotelId,
      setSelectedHotelId,
      zone,
      setZone,
      passengers,
      setPassengers,
      adults,
      setAdults,
      childrenUnder4,
      setChildrenUnder4,
      childrenOver4,
      setChildrenOver4,
      transportType,
      setTransportType,
      luggage,
      setLuggage,
      arrivalSchedule,
      setArrivalSchedule,
      returnSchedule,
      setReturnSchedule,
      firstName,
      setFirstName,
      lastName,
      setLastName,
      contactEmail,
      setContactEmail,
      phone,
      setPhone,
      originCity,
      setOriginCity,
      flightNumber,
      setFlightNumber,
      returnFlightNumber,
      setReturnFlightNumber,
      hasAdditionalLuggage,
      setHasAdditionalLuggage,
      additionalComments,
      setAdditionalComments,
      couponCode,
      setCouponCode,
      couponValidation,
      setCouponValidation,
      resetTransfer,
    }),
    [
      tripType,
      arrivalDate,
      arrivalTime,
      returnDate,
      returnTime,
      destinationType,
      address,
      selectedHotelId,
      zone,
      passengers,
      adults,
      childrenUnder4,
      childrenOver4,
      transportType,
      luggage,
      arrivalSchedule,
      returnSchedule,
      firstName,
      lastName,
      contactEmail,
      phone,
      originCity,
      flightNumber,
      returnFlightNumber,
      hasAdditionalLuggage,
      additionalComments,
      couponCode,
      couponValidation,
      resetTransfer,
    ],
  );

  return (
    <TransferContext.Provider value={value}>
      {children}
    </TransferContext.Provider>
  );
}
